/**
 * Browser Notification Helper
 * Manages browser notification permissions and display
 */

/**
 * Request browser notification permission
 * @returns {Promise<string>} - Permission status: 'granted', 'denied', or 'default'
 */
export const requestNotificationPermission = async () => {
    // Check if browser supports notifications
    if (!('Notification' in window)) {
        console.warn('This browser does not support desktop notifications');
        return 'denied';
    }

    // If already granted or denied, return current permission
    if (Notification.permission !== 'default') {
        return Notification.permission;
    }

    // Request permission
    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return 'denied';
    }
};

/**
 * Check if notifications are supported and permitted
 * @returns {boolean}
 */
export const areNotificationsEnabled = () => {
    return typeof window.Notification === 'function' && Notification.permission === 'granted';
};

/**
 * Show a browser notification
 * @param {string} title - Notification title
 * @param {object} options - Notification options
 * @returns {Notification|null}
 */
export const showNotification = (title, options = {}) => {
    if (!areNotificationsEnabled()) {
        return null;
    }

    try {
        const notification = new Notification(title, {
            badge: '/logo.png',
            icon: options.icon || '/logo.png',
            ...options,
        });

        return notification;
    } catch (error) {
        console.error('Error showing notification:', error);
        return null;
    }
};
